import React, { FC, useEffect, useMemo, useState } from 'react';
import { Box, Button, Grid, GridItem } from '@chakra-ui/react';

import { DistributorMutation } from '@app/api/models/distributor';
import { useAlert, useGuide } from '@app/hooks';
import { useEntityState } from '@app/hooks/use-entity-state';
import { sortEntityByName } from '@app/utils';
import TextField from '@app/components/common/text-field';
import SelectField from '@app/components/common/select-field';

import { DistributorFormInstance, SubChannelGuideRow } from './types';
import { mapFormToMutation } from './utils/mapInstance';

export interface DistributorFormProps {
  instance: Partial<DistributorFormInstance>;
  submitText?: string;
  loading: boolean;
  onSubmit: (payload: DistributorMutation) => void;
  onCancel: () => void;
}

const DistributorForm: FC<DistributorFormProps> = (props) => {
  const { instance, submitText, onSubmit, onCancel, loading } = props;
  const { warning } = useAlert();
  const [form, onChange, setForm] = useEntityState<DistributorFormInstance>(instance);
  const [guidesLoading, setGuidesLoading] = useState(true);
  const [rawChannels, setRawChannels] = useState<SubChannelGuideRow[]>([]);
  const [channels, setChannels] = useState<EntityBase[]>([]);
  const [subChannels, setSubChannels] = useState<EntityBase[]>([]);

  const getSubChannelGuide = useGuide('outlet_sub_channel');
  const getDistributorStatuses = useGuide('distributor_status');

  useEffect(() => {
    setForm(instance);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [instance]);

  useEffect(() => {
    (async () => {
      setGuidesLoading(true);
      const raw = (await getSubChannelGuide()) as SubChannelGuideRow[];
      setRawChannels(raw);
      setChannels(
        sortEntityByName(
          raw.reduce<EntityBase[]>((acc, item) => {
            if (acc.some(({ name }) => name === item.channel)) {
              return acc;
            }
            const cid = item.channel_id;
            return [
              ...acc,
              {
                id: cid != null ? cid : acc.length,
                name: item.channel,
              },
            ];
          }, [])
        )
      );
      setGuidesLoading(false);
    })();
  }, [getSubChannelGuide]);

  useEffect(() => {
    if (!rawChannels.length || !form.channel?.name) {
      setSubChannels([]);
      return;
    }
    setSubChannels(
      sortEntityByName(
        rawChannels.filter(
          (item) =>
            item.channel?.toUpperCase() ===
            (form.channel?.name || '').toUpperCase()
        )
      )
    );
  }, [rawChannels, form.channel?.name]);

  const channelOptions = useMemo(() => {
    if (!form.channel?.id && !form.channel?.name) {
      return channels;
    }
    if (channels.some((c) => String(c.id) === String(form.channel?.id))) {
      return channels;
    }
    return [form.channel as EntityBase, ...channels];
  }, [channels, form.channel]);

  const subChannelOptions = useMemo(() => {
    if (!form.sub_channel?.id && !form.sub_channel?.name) {
      return subChannels;
    }
    if (subChannels.some((s) => String(s.id) === String(form.sub_channel?.id))) {
      return subChannels;
    }
    return [form.sub_channel as EntityBase, ...subChannels];
  }, [subChannels, form.sub_channel]);

  const onChannelSelect = (value: EntityBase | undefined) => {
    onChange('channel')(value ?? null);
    onChange('sub_channel')(null);
  };

  const onSubmitClick = () => {
    if (!form.name?.trim()) {
      warning('Укажите наименование');
      return;
    }
    if (!form.status) {
      warning('Выберите статус');
      return;
    }
    onSubmit(mapFormToMutation(form, rawChannels));
  };

  const busy = loading || guidesLoading;

  return (
    <Grid gap={5} templateColumns={{ base: '1fr', md: '1fr 1fr' }}>
      <GridItem>
        <TextField
          id="name"
          label="Наименование"
          required
          value={form.name}
          onChange={onChange('name')}
        />
      </GridItem>
      <GridItem>
        <TextField
          id="legal_name"
          label="Юр. наименование"
          value={form.legal_name}
          onChange={onChange('legal_name')}
        />
      </GridItem>
      <GridItem>
        <TextField
          id="inn"
          label="ИНН"
          value={form.inn ?? ''}
          onChange={onChange('inn')}
        />
      </GridItem>
      <GridItem colSpan={{ base: 1, md: 2 }}>
        <TextField
          id="legal_address"
          label="Юридический адрес"
          value={form.legal_address}
          onChange={onChange('legal_address')}
        />
      </GridItem>
      <GridItem colSpan={{ base: 1, md: 2 }}>
        <TextField
          id="description"
          label="Описание"
          multiline
          rows={3}
          value={form.description}
          onChange={onChange('description')}
        />
      </GridItem>
      <GridItem>
        <SelectField
          id="channel"
          name="outlet_sub_channel"
          label="Канал"
          value={form.channel || undefined}
          options={channelOptions}
          onChange={onChannelSelect}
        />
      </GridItem>
      <GridItem>
        <SelectField
          id="sub_channel"
          name="outlet_sub_channel"
          label="Подканал"
          value={form.sub_channel || undefined}
          options={subChannelOptions}
          onChange={(v) => onChange('sub_channel')(v ?? null)}
        />
      </GridItem>
      <GridItem>
        <SelectField
          id="distributor_status"
          name="distributor_status"
          label="Статус"
          required
          value={form.status || undefined}
          options={form.status ? [form.status] : []}
          onFetchOptions={getDistributorStatuses}
          onChange={(v) => onChange('status')(v ?? null)}
        />
      </GridItem>
      <GridItem>
        <TextField
          id="phone"
          label="Телефон"
          value={form.phone}
          onChange={onChange('phone')}
        />
      </GridItem>
      <GridItem>
        <TextField
          id="email"
          label="Email"
          value={form.email}
          onChange={onChange('email')}
        />
      </GridItem>
      <GridItem>
        <TextField
          id="tm"
          label="ТМ"
          value={form.tm}
          onChange={onChange('tm')}
        />
      </GridItem>
      <GridItem>
        <TextField
          id="rm"
          label="РМ"
          value={form.rm}
          onChange={onChange('rm')}
        />
      </GridItem>
      <GridItem colSpan={{ base: 1, md: 2 }}>
        <TextField
          id="address_line"
          label="Фактический адрес"
          value={form.address?.address}
          onChange={(v) =>
            onChange('address')({
              ...form.address,
              address: v,
            })
          }
        />
      </GridItem>
      <GridItem>
        <TextField
          id="city"
          label="Город"
          value={typeof form.address?.city === 'string' ? form.address.city : ''}
          onChange={(v) =>
            onChange('address')({
              ...form.address,
              city: v,
            })
          }
        />
      </GridItem>
      <GridItem>
        <TextField
          id="region"
          label="Регион"
          value={
            typeof form.address?.region === 'string' ? form.address.region : ''
          }
          onChange={(v) =>
            onChange('address')({
              ...form.address,
              region: v,
            })
          }
        />
      </GridItem>
      <GridItem colSpan={{ base: 1, md: 2 }}>
        <Box display="flex" justifyContent="space-between">
          <Button
            isLoading={busy}
            colorScheme="brand"
            size="md"
            onClick={onSubmitClick}
          >
            {submitText || 'Сохранить'}
          </Button>
          <Button variant="ghost" size="md" onClick={onCancel}>
            Отмена
          </Button>
        </Box>
      </GridItem>
    </Grid>
  );
};

export default DistributorForm;
